import { inject, Injectable } from '@angular/core';

import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { combineLatest, filter, firstValueFrom, from, lastValueFrom, Observable, of, throwError, timer, TimeoutError } from 'rxjs';
import { catchError, concatMap, exhaustMap, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { TrovataAppState } from '@trovata/app/core/models/state.model';
import { PermissionId, PermissionMap } from '@trovata/app/features/settings/models/feature.model';
import { CustomerFeatureState } from '@trovata/app/features/settings/store/state/customer-feature.state';
import {
	ActivityDateGroup,
	ActivityEntry,
	Agent,
	AgentDetail,
	AgentDetailItem,
	AgentDetailResponse,
	AgentFormState,
	AgentInvocationDetailItem,
	AgentInvocationItem,
	AgentInvocationsResponse,
	AgentListItem,
	AgentsListTab,
	AgentsPanelType,
	AgentsResponse,
	AgentTriggerType,
	BuilderActiveTab,
	BuilderMode,
	ChatMessage,
	ChatRole,
	ExecutionHistoryEntry,
	InvocationDetailItem,
	InvocationDetailResponse,
	InvokeAgentResponse,
	PatchAgentBody,
	ParsedChatMessage,
	RuleConfigItem,
	TriggerConfig,
	triggerTypeToRuleType,
} from '@trovata/app/features/ai-agents/models/agents.model';
import { AgentStreamEvent, WebSocketConnectionError } from '@trovata/app/features/ai-agents/models/agents-websocket.model';
import { InlineChartData } from '@trovata/app/shared/models/inline-chart.model';
import { AgentsHttpService } from '@trovata/app/features/ai-agents/services/http/agents-http.service';
import { AgentsWebSocketService } from '@trovata/app/features/ai-agents/services/websocket/agents-websocket.service';
import {
	mapListItemToAgentDetail,
	mapDetailItemToAgentDetail,
	mapTriggerConfigToRuleConfig,
	mapInvocationsToActivityGroups,
	mapInvocationToActivityEntry,
	createPendingHistoryEntry,
	parseRecipients,
	formatRecipients,
	isTerminalInvocationStatus,
	isTriggerConfigComplete,
} from '@trovata/app/features/ai-agents/utils/agent-mapping.utils';
import { parseChatMessage, extractAgentName, extractStreamingFinprompt } from '@trovata/app/features/ai-agents/utils/finprompt-parser';
import { PollRequestKey } from '@trovata/app/shared/models/cancel-poll-request.model';
import { CancelPollRequestService } from '@trovata/app/shared/services/cancel-poll-request.service';
import {
	InitAgentsState,
	ClearAgentsState,
	ResetAgentsState,
	GetAgents,
	SelectAgent,
	SetAgentsListTab,
	StartChatSession,
	SendChatMessage,
	StopChatGeneration,
	ClearChatError,
	ClearChatSession,
	LoadAgentWithHistory,
	SaveAgent,
	SetAgentPaused,
	DeleteAgent,
	DuplicateAgent,
	OpenBuilderDialog,
	CloseBuilderDialog,
	SetBuilderActiveTab,
	UpdateConfigForm,
	PreviewAgent,
	InvokeAgent,
	PollInvocationResult,
	CancelInvocationPoll,
	DismissInvocationPollNotice,
	GetActivityFeed,
	SetActivityAgentFilter,
	SelectActivity,
	GetInvocationDetail,
	SetSelectedPanelType,
	OpenDetailPanel,
	CloseDetailPanel,
} from '@trovata/app/features/ai-agents/store/actions/agents.actions';

// ==========================================
// State Model
// ==========================================

/**
 * AgentsStateModel - State shape for the agents NGXS store.
 *
 * Defines all properties managed by AgentsState including agent entities,
 * chat session data, builder dialog state, and error tracking.
 *
 * Uses entity normalization pattern:
 * - agentEntities: Record<string, AgentDetail> for O(1) lookups
 * - agentIds: Ordered ID array for list display
 */
export class AgentsStateModel {
	/** Normalized entity map for O(1) agent lookups. */
	agentEntities: Record<string, AgentDetail>;

	/** Ordered array of agent IDs for list display. */
	agentIds: string[];

	/** Currently selected agent ID, or null if none selected. */
	selectedAgentId: string | null;

	/** Currently active tab on the agents list page. */
	selectedTab: AgentsListTab;

	/** Whether the agent list is loading (InitAgentsState / GetAgents). */
	isListLoading: boolean;

	/** Whether a single agent detail is loading (LoadAgentWithHistory). */
	isDetailLoading: boolean;

	/** Internal to AgentsState — do not expose via facade. Whether agents data has been cached from a prior fetch. */
	isCached: boolean;

	/** Active chat session ID, or null if no session. */
	chatSessionId: string | null;

	/** Agent ID associated with the active chat session. */
	chatAgentId: string | null;

	/** Parsed chat messages for the active session. */
	chatMessages: ParsedChatMessage[];

	/** Whether the AI is currently generating a response. */
	isChatGenerating: boolean;

	/** Whether a chat session is currently being initialized. */
	isChatSessionLoading: boolean;

	/** Builder dialog mode, or null if dialog is closed. */
	builderMode: BuilderMode | null;

	/** Active tab within the builder dialog. */
	builderActiveTab: BuilderActiveTab;

	/** Current state of the builder config form. */
	configForm: AgentFormState;

	/** Preview output content, or null if no preview. */
	previewContent: string | null;

	/** Whether the preview is currently loading. */
	isPreviewLoading: boolean;

	/** Whether the agents list API returned an error. */
	agentsListError: boolean;

	/** Whether conversation history was unavailable and local fallback was used. */
	chatHistoryUnavailable: boolean;

	/** Chat error message, or null if no error. */
	chatError: string | null;

	/** Preview error message, or null if no error. */
	previewError: string | null;

	/** Accumulated streaming content for the current chat response, or null when not streaming. */
	streamingChatContent: string | null;

	/** Accumulated streaming content for the current preview, or null when not streaming. */
	streamingPreviewContent: string | null;

	/**
	 * Short label for the most recent tool of the current preview run (replaced per tool), or null
	 * when idle. The preview tab shows it only while the run is loading or streaming, so every way a
	 * run can end — completion, error, teardown — clears it, as does starting a new preview or a reset.
	 */
	previewProgressMessage: string | null;

	/** Inline charts delivered on the preview's terminal frame; lifecycle paired with `previewContent`. */
	previewInlineCharts: Readonly<Record<string, InlineChartData>> | null;

	/**
	 * Degrade warning from the preview's terminal frame (e.g. charts omitted because the payload was
	 * oversized), or null when the run finished clean. Lifecycle paired with `previewContent`.
	 */
	previewWarningMessage: string | null;

	/** Short label for the tool the builder-chat turn is currently executing, or null when idle. */
	chatProgressMessage: string | null;

	/** Whether the activity feed is currently loading. */
	isActivityLoading: boolean;

	/** Whether the activity feed API returned an error. */
	activityFeedError: boolean;

	/** Activity feed grouped by date. */
	activityGroups: ActivityDateGroup[];

	/** Agent IDs the activity feed is currently filtered to. Empty array means no filter (all agents shown). */
	activityFilterAgentIds: readonly string[];

	/** Currently selected activity entry ID. */
	selectedActivityId: string | null;

	/** Full detail (including inline charts) for the selected activity's invocation; null while unfetched or when the fetch failed. */
	selectedInvocationDetail: InvocationDetailItem | null;

	/** Whether the invocation-detail fetch is in flight. */
	isInvocationDetailLoading: boolean;

	/** Currently selected panel type for detail view. */
	selectedPanelType: AgentsPanelType | null;

	/** Whether the right-hand detail panel is currently visible. */
	detailPanelOpen: boolean;

	/**
	 * The agent whose run is in flight or being polled, or null when none is. Keyed by agent
	 * rather than a feature-wide flag: only that agent's row shows a run in progress, and only
	 * a repeat click on that same agent is dropped. One run is watched at a time (the poll is a
	 * single keyed subject), so starting a run elsewhere hands the watch over — the previous
	 * run continues server-side and its result still lands in the activity feed.
	 */
	invokingAgentId: string | null;

	/**
	 * The invocation the UI is currently watching, or null when it is watching none.
	 * `invokingAgentId` says which agent's run is in flight; this says which run, so a run the
	 * user walked away from cannot take over — or tear down — the run that replaced it.
	 */
	watchedInvocationId: string | null;

	/**
	 * The invocation the "still running" notice is currently about, or null when no notice is
	 * showing. Holds the id rather than a flag so a later feed refresh can tell that this run
	 * finished and retire the notice; the run may still complete server-side. Purely the
	 * notice's visibility — dismissing it clears this and nothing else.
	 */
	pollTimedOutInvocationId: string | null;

	/**
	 * Invocations the poll has settled — it either saw them reach a terminal status or ran out of
	 * budget on them — and will not arm a watch for again while they are still in this bounded
	 * ledger (`SETTLED_INVOCATION_LEDGER_LIMIT`). Re-arming either kind is wrong — a finished
	 * run has nothing left to poll (a lagging feed can still list it as in flight), and a run we
	 * gave up on would be handed another full budget. Separate from the notice above so
	 * dismissing the message cannot release a run back into the re-arm pool.
	 */
	settledInvocationIds: readonly string[];

	/** Identifiers of agents with an in-flight `SetAgentPaused` PATCH. Multiple entries allow concurrent pause/resume across rows without one row clearing another's loading state. */
	pausingAgentIds: readonly string[];
}

// ==========================================
// Module-level Constants
// ==========================================

/** First poll delay in `pollInvocationResult`. Typical runs finish in 76–126s, so early ticks are cheap probes. */
const POLL_INITIAL_DELAY_MS: number = 5_000;

/** Backoff growth per tick: 5s → 7.5s → 11.25s → 15s (capped). */
const POLL_BACKOFF_FACTOR: number = 1.5;

/** Delay cap — 15s keeps result-detection latency for a typical run under one interval. */
const POLL_MAX_INTERVAL_MS: number = 15_000;

/**
 * Total polling budget. Matches the backend's 900s execution cap: the poll
 * never abandons a run the backend might still finish. Giving up at the
 * ceiling is visible (`invocationPollTimedOut`), never a silent stall.
 */
const POLL_TOTAL_CEILING_MS: number = 900_000;

/**
 * Builds the backoff schedule: delays grow by `factor` from `initialMs` up to
 * `maxMs`, and the sequence stops before the cumulative total would exceed
 * `ceilingMs`. Exported for tests (known-answer coverage of the schedule).
 */
export function buildPollDelays(initialMs: number, factor: number, maxMs: number, ceilingMs: number): readonly number[] {
	const delays: number[] = [];
	let next: number = initialMs;
	let total: number = 0;
	while (total + next <= ceilingMs) {
		delays.push(next);
		total += next;
		next = Math.min(Math.round(next * factor), maxMs);
	}
	return delays;
}

/** The realized poll schedule: 5s, 7.5s, 11.25s, then 15s ticks — ~61 requests over ~894s. */
const POLL_DELAYS_MS: readonly number[] = buildPollDelays(POLL_INITIAL_DELAY_MS, POLL_BACKOFF_FACTOR, POLL_MAX_INTERVAL_MS, POLL_TOTAL_CEILING_MS);

/**
 * How many settled invocations to remember. The ledger only has to outlive the activity
 * feed's own window — that endpoint returns 50 runs unless asked for more — so this leaves
 * several pages of slack while keeping the list bounded across a long-lived session.
 */
const SETTLED_INVOCATION_LEDGER_LIMIT: number = 200;

/**
 * Picks the run the user most likely acted on last out of the ones still in flight.
 *
 * Ranks by the instant each run was created, not by comparing the timestamps as text:
 * `createdAt` is typed as any ISO string, and as text '…12:00:00Z' outranks
 * '…11:00:00-07:00' even though the latter is six hours later. A run the feed dated with
 * nothing cannot be placed in time at all, so it does not take part in the ranking — and
 * when none of them can be dated there is nothing to rank, so the feed's own order stands
 * (the endpoint returns runs newest first).
 *
 * @param inFlight - Non-terminal runs the feed returned, in feed order; must not be empty
 * @returns The newest of them
 */
function newestRun(inFlight: readonly AgentInvocationDetailItem[]): AgentInvocationDetailItem {
	const dated: AgentInvocationDetailItem[] = inFlight.filter((invocation: AgentInvocationDetailItem) => !Number.isNaN(createdAtMs(invocation)));
	if (dated.length === 0) {
		return inFlight[0];
	}
	return dated.reduce((latest: AgentInvocationDetailItem, invocation: AgentInvocationDetailItem) =>
		createdAtMs(invocation) > createdAtMs(latest) ? invocation : latest
	);
}

/** Epoch milliseconds for a run's creation time, `NaN` when it has none or it does not parse. */
function createdAtMs(invocation: AgentInvocationDetailItem): number {
	return invocation.createdAt ? Date.parse(invocation.createdAt) : Number.NaN;
}

/**
 * Adds an invocation to the settled ledger, keeping the newest entries within the limit.
 *
 * @param settled - Ledger as it stands
 * @param invocationId - Run that has finished or been given up on
 * @returns The ledger including that run, unchanged when it was already there
 */
function withSettledInvocation(settled: readonly string[], invocationId: string): readonly string[] {
	if (settled.includes(invocationId)) {
		return settled;
	}
	return [...settled, invocationId].slice(-SETTLED_INVOCATION_LEDGER_LIMIT);
}

/**
 * Consecutive invocation-detail failures before the poll switches to the agent-detail
 * route. Three ticks is ~24s: long enough to ride out a transient outage, short enough
 * that a backend without the route costs a run barely more than its own runtime.
 */
const POLL_DETAIL_FALLBACK_THRESHOLD: number = 3;

/**
 * Shared shape between `AgentListItem` and `AgentDetailItem` used to build an `Agent`.
 *
 * Both API response shapes include these 4 fields; the helper `buildAgentFromApiItem`
 * accepts either kind without caring which endpoint it came from.
 */
interface AgentApiSource {
	readonly agentId: string;
	readonly sessionId: string;
	readonly name: string;
	readonly prompt: string;
}

// ==========================================
// Default State
// ==========================================

const DEFAULT_AGENTS_STATE: AgentsStateModel = {
	agentEntities: {},
	agentIds: [],
	selectedAgentId: null,
	selectedTab: AgentsListTab.activity,
	isListLoading: false,
	isDetailLoading: false,
	isCached: false,
	chatSessionId: null,
	chatAgentId: null,
	chatMessages: [],
	isChatGenerating: false,
	isChatSessionLoading: false,
	builderMode: null,
	builderActiveTab: 'configuration',
	configForm: {
		name: '',
		instructions: '',
		triggerConfig: {},
		delivery: { recipients: '' },
		isDirty: false,
		isValid: false,
	},
	previewContent: null,
	isPreviewLoading: false,
	agentsListError: false,
	chatHistoryUnavailable: false,
	chatError: null,
	previewError: null,
	streamingChatContent: null,
	streamingPreviewContent: null,
	previewProgressMessage: null,
	previewInlineCharts: null,
	previewWarningMessage: null,
	chatProgressMessage: null,
	isActivityLoading: false,
	activityFeedError: false,
	activityGroups: [],
	activityFilterAgentIds: [],
	selectedActivityId: null,
	selectedInvocationDetail: null,
	isInvocationDetailLoading: false,
	selectedPanelType: null,
	detailPanelOpen: false,
	invokingAgentId: null,
	watchedInvocationId: null,
	pollTimedOutInvocationId: null,
	settledInvocationIds: [],
	pausingAgentIds: [],
};

// ==========================================
// State Definition
// ==========================================

/**
 * AgentsState — Central state management for the AI Agents feature.
 *
 * Manages agent entities, chat sessions (configuration + preview streams),
 * builder dialog state, manual invocations with polling, and the activity feed.
 *
 * Key responsibilities:
 * - Stores agents in a normalized map for O(1) lookups and in-place updates
 * - Orchestrates WebSocket streaming for chat and preview via AgentsWebSocketService
 * - Polls `GET /agents/invocations/{id}` with backoff to detect invocation completion
 * - Derives config-form state from the last `finprompt` in a session's history
 *
 * State lifecycle:
 * - `InitAgentsState` — loads once per session, gated by `readAgents` permission
 * - `ClearAgentsState` — full reset (logout/tenant switch); aborts any active streams + polling
 * - `ResetAgentsState` — re-initializes while clearing the `isCached` flag
 *
 * Note: `getAgents` rebuilds `agentEntities` from each response (agents
 * absent from the latest response are dropped), but for agents still present
 * it preserves their prior `executionHistory` since the list endpoint does
 * not return invocations. See `getAgents` for details.
 */
@State<AgentsStateModel>({
	name: 'agents',
	defaults: DEFAULT_AGENTS_STATE,
})
@Injectable()
export class AgentsState {
	// ==========================================
	// Services (injected)
	// ==========================================

	private readonly agentsHttpService: AgentsHttpService = inject(AgentsHttpService);
	private readonly agentsWsService: AgentsWebSocketService = inject(AgentsWebSocketService);
	private readonly store: Store = inject(Store);
	private readonly cancelPollRequestService: CancelPollRequestService = inject(CancelPollRequestService);

	// ==========================================
	// Properties
	// ==========================================

	/**
	 * Rises by one on every invoke and every deliberate stop. An invoke POST captures the value it
	 * started with and re-reads it when the response lands, which is the only way to tell whether
	 * the user is still waiting on *that* run — the invocation id does not exist until then.
	 */
	private invokeGeneration: number = 0;

	/**
	 * Rises by one on every preview run. A run that ends without a terminal event reads it back to
	 * check whether it is still the run the UI is waiting on before touching the loading flag.
	 */
	private previewGeneration: number = 0;

	/**
	 * Rises by one on every invocation poll. Polls share one cancel key and one watch slot, so a
	 * finishing poll reads it back to check it is still the current one before running teardown —
	 * otherwise its cleanup lands on the poll that replaced it.
	 */
	private pollGeneration: number = 0;

	/**
	 * Rises by one on every invocation-detail fetch. The detail-loading flag is shared across
	 * fetches, so only the newest fetch — success or failure — may clear it: an older run
	 * completing late must not blank the spinner a newer fetch still owns. Deselecting dispatches
	 * no successor fetch, which leaves the in-flight one the newest, so its completion still
	 * clears the flag (an id check against the selection would strand the spinner there).
	 */
	private detailGeneration: number = 0;

	// ==========================================
	// Selectors
	// ==========================================

	/** Returns all agents denormalized from the entity map, preserving agentIds order. */
	@Selector()
	static agents(state: AgentsStateModel): AgentDetail[] {
		return state.agentIds.map((id: string) => state.agentEntities[id]).filter(Boolean);
	}

	/** Returns the currently selected agent, or null if none selected. */
	@Selector()
	static selectedAgent(state: AgentsStateModel): AgentDetail | null {
		if (!state.selectedAgentId) {
			return null;
		}
		return state.agentEntities[state.selectedAgentId] ?? null;
	}

	/**
	 * O(1) lookup for an agent by ID.
	 *
	 * This is a parameterized selector — it returns a function that takes an
	 * `agentId` parameter. The double parenthesis at the call site is intentional:
	 * the outer call selects the function, the inner call invokes it.
	 *
	 * @returns Function that takes `agentId` and returns `AgentDetail | undefined`
	 *
	 * Usage:
	 *   // Snapshot (synchronous, non-reactive):
	 *   const agent: AgentDetail | undefined = this.store.selectSnapshot(AgentsState.getAgentById)(agentId);
	 *   if (agent) {
	 *     // Use agent data
	 *   }
	 *
	 *   // Observable (reactive):
	 *   this.agent$ = this.store.select(AgentsState.getAgentById)(agentId);
	 *
	 *   // Signal (reactive, NGXS v4+):
	 *   this.agent = this.store.selectSignal(AgentsState.getAgentById)(agentId);
	 */
	@Selector()
	static getAgentById(state: AgentsStateModel): (agentId: string) => AgentDetail | undefined {
		return (agentId: string): AgentDetail | undefined => state.agentEntities[agentId];
	}

	/** Returns whether the agent list is loading. */
	@Selector()
	static isListLoading(state: AgentsStateModel): boolean {
		return state.isListLoading;
	}

	/** Returns whether a single agent detail is loading. */
	@Selector()
	static isDetailLoading(state: AgentsStateModel): boolean {
		return state.isDetailLoading;
	}

	/** Internal to AgentsState — do not expose via facade. Returns whether agents data has been cached. */
	@Selector()
	static isCached(state: AgentsStateModel): boolean {
		return state.isCached;
	}

	/** Returns the currently active agents list tab. */
	@Selector()
	static selectedTab(state: AgentsStateModel): AgentsListTab {
		return state.selectedTab;
	}

	/** Returns the parsed chat messages for the active session. */
	@Selector()
	static chatMessages(state: AgentsStateModel): ParsedChatMessage[] {
		return state.chatMessages;
	}

	/** Returns the active chat session ID. */
	@Selector()
	static chatSessionId(state: AgentsStateModel): string | null {
		return state.chatSessionId;
	}

	/** Returns the agent ID associated with the active chat session. */
	@Selector()
	static chatAgentId(state: AgentsStateModel): string | null {
		return state.chatAgentId;
	}

	/** Returns whether the AI is currently generating a response. */
	@Selector()
	static isChatGenerating(state: AgentsStateModel): boolean {
		return state.isChatGenerating;
	}

	/** Returns whether a chat session is currently being initialized. */
	@Selector()
	static isChatSessionLoading(state: AgentsStateModel): boolean {
		return state.isChatSessionLoading;
	}

	/** Returns the builder dialog mode, or null if closed. */
	@Selector()
	static builderMode(state: AgentsStateModel): BuilderMode | null {
		return state.builderMode;
	}

	/** Returns the active tab within the builder dialog. */
	@Selector()
	static builderActiveTab(state: AgentsStateModel): BuilderActiveTab {
		return state.builderActiveTab;
	}

	/** Returns the current builder config form state. */
	@Selector()
	static configForm(state: AgentsStateModel): AgentFormState {
		return state.configForm;
	}

	/** Returns the preview output content. */
	@Selector()
	static previewContent(state: AgentsStateModel): string | null {
		return state.previewContent;
	}

	/** Returns whether the preview is currently loading. */
	@Selector()
	static isPreviewLoading(state: AgentsStateModel): boolean {
		return state.isPreviewLoading;
	}

	/**
	 * Returns whether the config form has enough data to save the agent.
	 *
	 * In `new` mode the `chatAgentId` is minted lazily via
	 * `AgentsFacadeService.ensureChatSession()` when the user first hits Save
	 * or sends a chat, so it is intentionally NOT required here — gating on
	 * it would permanently disable the save button before the lazy create
	 * ever runs. In `continue` / `edit` modes the id is already set by
	 * `LoadAgentWithHistory`, so this selector lights up as soon as
	 * instructions are non-empty.
	 */
	@Selector()
	static canSave(state: AgentsStateModel): boolean {
		if (state.configForm.instructions === '') {
			return false;
		}
		// Schedules keep their long-standing instructions-only gate and can be saved as drafts while incomplete. A
		// balance or data-integrity trigger must be complete first. An incomplete one maps either to a rule the API
		// rejects (no accounts or conditions is a 400) or, with its condition missing, to no rule at all; and since the
		// builder's form state isn't persisted, a draft would lose the half-built trigger.
		const triggerConfig: TriggerConfig = state.configForm.triggerConfig;
		return !triggerConfig.type || triggerConfig.type === AgentTriggerType.scheduled || isTriggerConfigComplete(triggerConfig);
	}

	/** Returns whether the config form has unsaved changes. */
	@Selector()
	static hasUnsavedChanges(state: AgentsStateModel): boolean {
		return state.configForm.isDirty;
	}

	/** Returns whether the agents list API returned an error. */
	@Selector()
	static agentsListError(state: AgentsStateModel): boolean {
		return state.agentsListError;
	}

	/** Returns whether conversation history was unavailable (local fallback used). */
	@Selector()
	static chatHistoryUnavailable(state: AgentsStateModel): boolean {
		return state.chatHistoryUnavailable;
	}

	/** Returns the chat error message. */
	@Selector()
	static chatError(state: AgentsStateModel): string | null {
		return state.chatError;
	}

	/** Returns the preview error message. */
	@Selector()
	static previewError(state: AgentsStateModel): string | null {
		return state.previewError;
	}

	/** Returns the accumulated streaming chat content, or null when not streaming. */
	@Selector()
	static streamingChatContent(state: AgentsStateModel): string | null {
		return state.streamingChatContent;
	}

	/** True while the chat stream is actively writing into a `<finprompt>` block — used to disable the instructions textarea. */
	@Selector()
	static isStreamingFinprompt(state: AgentsStateModel): boolean {
		return state.streamingChatContent !== null && state.streamingChatContent.includes('<finprompt>');
	}

	/** Returns the accumulated streaming preview content, or null when not streaming. */
	@Selector()
	static streamingPreviewContent(state: AgentsStateModel): string | null {
		return state.streamingPreviewContent;
	}

	/** Returns the label of the tool the preview run is currently executing, or null when idle. */
	@Selector()
	static previewProgressMessage(state: AgentsStateModel): string | null {
		return state.previewProgressMessage;
	}

	/** Returns the inline charts delivered with the completed preview, or null when the run produced none. */
	@Selector()
	static previewInlineCharts(state: AgentsStateModel): Readonly<Record<string, InlineChartData>> | null {
		return state.previewInlineCharts;
	}

	/** Returns the degrade warning from the completed preview's terminal frame, or null when it finished clean. */
	@Selector()
	static previewWarningMessage(state: AgentsStateModel): string | null {
		return state.previewWarningMessage;
	}

	/** Returns the label of the tool the builder-chat turn is currently executing, or null when idle. */
	@Selector()
	static chatProgressMessage(state: AgentsStateModel): string | null {
		return state.chatProgressMessage;
	}

	/** Returns whether the activity feed is currently loading. */
	@Selector()
	static isActivityLoading(state: AgentsStateModel): boolean {
		return state.isActivityLoading;
	}

	/** Returns whether the last activity-feed fetch failed. */
	@Selector()
	static activityFeedError(state: AgentsStateModel): boolean {
		return state.activityFeedError;
	}

	/** Returns the activity feed grouped by date. */
	@Selector()
	static activityGroups(state: AgentsStateModel): ActivityDateGroup[] {
		return state.activityGroups;
	}

	/** Agent IDs the activity feed is filtered to; empty array means unfiltered. */
	@Selector()
	static activityFilterAgentIds(state: AgentsStateModel): readonly string[] {
		return state.activityFilterAgentIds;
	}

	/** Returns the currently selected activity entry, or null if none selected. */
	@Selector()
	static selectedActivity(state: AgentsStateModel): ActivityEntry | null {
		if (!state.selectedActivityId) {
			return null;
		}
		for (const group of state.activityGroups) {
			const entry: ActivityEntry | undefined = group.entries.find((e: ActivityEntry) => e.id === state.selectedActivityId);
			if (entry) {
				return entry;
			}
		}
		// A run reached by deep link can be older than the loaded feed window — links in scheduled
		// report emails routinely are. The detail fetch resolves it on its own, so render from that
		// rather than dropping the user on the agent instead of the run they clicked.
		const detail: InvocationDetailItem | null = state.selectedInvocationDetail;
		return detail && detail.invocationId === state.selectedActivityId ? mapInvocationToActivityEntry(detail) : null;
	}

	/** Full detail for the selected activity's invocation, or null while unfetched / after a failed fetch. */
	@Selector()
	static selectedInvocationDetail(state: AgentsStateModel): InvocationDetailItem | null {
		return state.selectedInvocationDetail;
	}

	/** Whether the invocation-detail fetch is in flight. */
	@Selector()
	static isInvocationDetailLoading(state: AgentsStateModel): boolean {
		return state.isInvocationDetailLoading;
	}

	/** Returns the currently selected panel type. */
	@Selector()
	static selectedPanelType(state: AgentsStateModel): AgentsPanelType | null {
		return state.selectedPanelType;
	}

	/** Returns whether the right-hand detail panel is currently visible. */
	@Selector()
	static detailPanelOpen(state: AgentsStateModel): boolean {
		return state.detailPanelOpen;
	}

	/** Returns the agent whose run is in flight or being polled, or null when none is. */
	@Selector()
	static invokingAgentId(state: AgentsStateModel): string | null {
		return state.invokingAgentId;
	}

	/** True when polling gave up while the run was still non-terminal — the run may still finish server-side. */
	@Selector()
	static invocationPollTimedOut(state: AgentsStateModel): boolean {
		return state.pollTimedOutInvocationId !== null;
	}

	/** Returns the identifiers of agents with an in-flight pause/resume PATCH. */
	@Selector()
	static pausingAgentIds(state: AgentsStateModel): readonly string[] {
		return state.pausingAgentIds;
	}

	// ==========================================
	// Lifecycle Action Handlers
	// ==========================================

	/**
	 * Initializes the agents state when the application starts.
	 *
	 * Waits for app readiness and the user's permission map, then delegates
	 * the actual fetch to `GetAgents`. Early-returns if the state is already
	 * cached from a prior fetch (survives route changes), or if the user
	 * lacks the `readAgents` permission.
	 *
	 * @param context - NGXS state context
	 * @returns Promise<void>
	 *
	 * Side effects:
	 * - No-op if `isCached` is already true or if the user lacks `readAgents`
	 * - Otherwise dispatches `GetAgents`; see `getAgents` for its side effects
	 *   (loading flag, entity-map rebuild, cache + error flag handling)
	 */
	@Action(InitAgentsState)
	async initAgentsState(context: StateContext<AgentsStateModel>): Promise<void> {
		const state: AgentsStateModel = context.getState();
		if (state.isCached) {
			return;
		}

		const appReady$: Observable<boolean> = this.store.select((appState: TrovataAppState) => appState.core.appReady);
		const permissions$: Observable<PermissionMap> = this.store.select(CustomerFeatureState.permissionIds);
		const [, permissions]: [boolean, PermissionMap] = await firstValueFrom(
			combineLatest([appReady$, permissions$]).pipe(
				filter(([ready, perms]: [boolean, PermissionMap]) => !!ready && !!perms),
				take(1)
			)
		);

		if (!permissions.has(PermissionId.readAgents)) {
			return;
		}

		await lastValueFrom(context.dispatch(new GetAgents()));
	}

	/** Clears all agents state data. */
	@Action(ClearAgentsState)
	clearAgentsState(context: StateContext<AgentsStateModel>): void {
		// An invoke POST still in flight across a logout belongs to the session being torn down.
		this.invokeGeneration += 1;
		this.cancelPollRequestService.cancelPollSubject(PollRequestKey.PollInvocationResult);
		this.agentsWsService.abortStream();
		context.setState(DEFAULT_AGENTS_STATE);
	}

	/** Resets agents state to initial values then re-initializes. */
	@Action(ResetAgentsState)
	resetAgentsState(context: StateContext<AgentsStateModel>): Observable<void> {
		context.patchState({ isCached: false });
		return context.dispatch(new InitAgentsState());
	}

	// ==========================================
	// Agents List Action Handlers
	// ==========================================

	/**
	 * Fetches the list of agents from the API (always refreshes, ignores cache).
	 *
	 * The single fetch path for the agents list. Dispatched by `initAgentsState`
	 * after its app-ready wait + permission check, and by the facade when a
	 * caller needs an explicit refetch (e.g. after save / delete). Does not
	 * gate on the permission flag — callers that need permission checks do
	 * them before dispatching.
	 *
	 * @param context - NGXS state context
	 * @returns Observable that completes when the state update is applied
	 *
	 * Side effects:
	 * - Sets `isListLoading: true` and clears `agentsListError` before the API
	 *   call so retries after a prior failure don't flash the error state
	 * - Rebuilds `agentEntities` + `agentIds` from the response
	 * - Preserves each agent's prior `executionHistory`, since the list endpoint
	 *   does not include invocations (only `getAgentDetail` does)
	 * - Sets `isCached: true` on success, `agentsListError: true` on failure
	 */
	@Action(GetAgents)
	getAgents(context: StateContext<AgentsStateModel>): Observable<void> {
		context.patchState({ isListLoading: true, agentsListError: false });
		return this.agentsHttpService.listAgents().pipe(
			tap((response: AgentsResponse) => {
				const currentState: AgentsStateModel = context.getState();
				const agents: AgentDetail[] = response.agents.map((item: AgentListItem) => {
					const existingAgent: AgentDetail | undefined = currentState.agentEntities[item.agentId];
					const agent: Agent = this.buildAgentFromApiItem(item, existingAgent?.name);
					const detail: AgentDetail = mapListItemToAgentDetail(agent, item);
					// Preserve execution history from prior LoadAgentWithHistory calls,
					// since the list endpoint does not include invocations.
					if (existingAgent?.executionHistory?.length) {
						return { ...detail, executionHistory: existingAgent.executionHistory };
					}
					return detail;
				});
				const { entities: agentEntities, ids: agentIds }: { entities: Record<string, AgentDetail>; ids: string[] } = this.normalizeAgents(agents);
				context.patchState({
					agentEntities,
					agentIds,
					isListLoading: false,
					isCached: true,
					agentsListError: false,
				});
			}),
			map(() => undefined),
			catchError(() => {
				context.patchState({
					isListLoading: false,
					isCached: false,
					agentsListError: true,
				});
				return of(undefined);
			})
		);
	}

	/** Selects an agent by ID. */
	@Action(SelectAgent)
	selectAgent(context: StateContext<AgentsStateModel>, action: SelectAgent): void {
		context.patchState({ selectedAgentId: action.agentId });
	}

	/** Sets the active tab on the agents list page. */
	@Action(SetAgentsListTab)
	setAgentsListTab(context: StateContext<AgentsStateModel>, action: SetAgentsListTab): void {
		context.patchState({ selectedTab: action.tab });
	}

	// ==========================================
	// Chat Session Action Handlers
	// ==========================================

	/**
	 * Starts a new chat session by creating a temporary agent server-side.
	 *
	 * Dispatched when the user opens the builder dialog in `new` mode and by
	 * the chat component's retry-session handler. The returned `sessionId`
	 * anchors all subsequent WebSocket chat messages; the `identifier` becomes
	 * the agent's real ID once saved.
	 *
	 * @param context - NGXS state context
	 * @returns Observable of the created agent; rethrows on failure so the caller
	 *   can surface an error toast
	 *
	 * Side effects:
	 * - Sets `isChatSessionLoading: true` + clears `chatError` before the API call
	 * - Populates `chatSessionId` / `chatAgentId` and clears `chatMessages` on success
	 * - Sets `chatError` to a user-facing message and rethrows on failure
	 */
	@Action(StartChatSession)
	startChatSession(context: StateContext<AgentsStateModel>): Observable<Agent> {
		context.patchState({ isChatSessionLoading: true, chatError: null });

		return this.agentsHttpService.createAgent().pipe(
			tap((response: Agent) => {
				context.patchState({
					chatSessionId: response.sessionId,
					chatAgentId: response.identifier,
					chatMessages: [],
					isChatGenerating: false,
					isChatSessionLoading: false,
					chatError: null,
					chatProgressMessage: null,
				});
			}),
			catchError((error: unknown) => {
				context.patchState({
					chatError: 'Failed to start chat session. Please try again.',
					isChatSessionLoading: false,
				});
				return throwError(() => error);
			})
		);
	}

	/**
	 * Sends a chat message and processes the AI response via WebSocket streaming.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing the user's prompt text
	 * @returns Observable that completes when the stream terminates (success or error)
	 *
	 * Side effects:
	 * - Optimistically appends the user's message to `chatMessages` (not reverted on failure —
	 *   keeps the message visible so the user can retry without re-typing)
	 * - Accumulates `delta` events into `streamingChatContent`
	 * - On `complete`: parses the assistant response; if the reply contains a
	 *   `finprompt`, updates `configForm.instructions` and (only if the current
	 *   name is empty) extracts a name from the response text. Also clears
	 *   `previewContent` / `previewError` when the finprompt changes so the
	 *   preview tab doesn't show a stale render.
	 * - On `error` event or transport failure: keeps any text that already streamed as a
	 *   partial assistant message, then sets `chatError` with a subtype-specific message
	 */
	@Action(SendChatMessage)
	sendChatMessage(context: StateContext<AgentsStateModel>, action: SendChatMessage): Observable<void> {
		const sessionId: string | undefined = context.getState().chatSessionId ?? undefined;

		// Step 1: Add the user's message to the transcript optimistically.
		this.applyOptimisticUserMessage(context, action.prompt);

		// Step 2: Open the WebSocket stream and react to each event.
		return this.agentsWsService.sendConfigurationChat(action.prompt, sessionId).pipe(
			tap((event: AgentStreamEvent) => {
				switch (event.type) {
					case 'delta':
						this.applyChatDelta(context, event.content);
						break;
					case 'complete':
						this.applyChatStreamComplete(context, event.inlineCharts);
						break;
					case 'error':
						context.patchState({
							chatMessages: this.transcriptWithStreamedPartial(context.getState()),
							chatError: event.message || 'Something went wrong. Please try again.',
							isChatGenerating: false,
							streamingChatContent: null,
							chatProgressMessage: null,
						});
						break;
					case 'processing':
						// A labeled frame names the tool the turn is currently executing; label-less
						// frames are keep-alive pings and preserve the last shown label.
						if (event.message) {
							context.patchState({ chatProgressMessage: event.message });
						}
						break;
					default: {
						// Compile-time exhaustiveness stays (`never`), but at runtime a newer backend
						// may ship frame kinds this build doesn't know — ignore them, never throw.
						const exhaustive: never = event;
						console.warn('[AgentsState] Ignoring unknown AgentStreamEvent', exhaustive);
						break;
					}
				}
			}),
			map(() => undefined),
			catchError((error: unknown) => {
				this.applyChatStreamFatalError(context, error);
				return of(undefined);
			})
		);
	}

	/**
	 * Clears `chatError` — used by the facade's `ensureChatSession` in silent
	 * mode so the inline "Failed to connect" banner doesn't linger after a
	 * caller (e.g. `onSaveAction`) has surfaced its own error affordance.
	 */
	@Action(ClearChatError)
	clearChatError(context: StateContext<AgentsStateModel>): void {
		context.patchState({ chatError: null });
	}

	/** Stops the current AI response generation and preserves any partial content. */
	@Action(StopChatGeneration)
	stopChatGeneration(context: StateContext<AgentsStateModel>): void {
		// Chat-only: the builder dialog can have a preview streaming in the other tab, and stopping
		// the chat answer must not cancel it.
		this.agentsWsService.abortChatStream();
		context.patchState({
			chatMessages: this.transcriptWithStreamedPartial(context.getState()),
			isChatGenerating: false,
			streamingChatContent: null,
			chatProgressMessage: null,
		});
	}

	/** Clears the current chat session and resets all chat state to defaults. */
	@Action(ClearChatSession)
	clearChatSession(context: StateContext<AgentsStateModel>): void {
		this.agentsWsService.abortStream();
		context.patchState({
			chatSessionId: null,
			chatAgentId: null,
			chatMessages: [],
			isChatGenerating: false,
			isChatSessionLoading: false,
			chatError: null,
			streamingChatContent: null,
			streamingPreviewContent: null,
			chatProgressMessage: null,
		});
	}

	// ==========================================
	// Agent CRUD Action Handlers
	// ==========================================

	/**
	 * Loads a single agent with its full chat history.
	 *
	 * Dispatched when the user opens the builder dialog in `continue`/`edit` mode.
	 * The response drives both the chat transcript and the config-form state.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing the `agentId` to load
	 * @returns Observable that completes after success or local-fallback branch
	 *
	 * Side effects:
	 * - Sets `isDetailLoading: true` before the API call
	 * - On success: upserts the agent into `agentEntities` and — iff the user is
	 *   still on the same agent — rehydrates `chatMessages`, `chatSessionId`,
	 *   `configForm`, and clears related flags
	 * - On failure: falls back to the local entity if available (setting
	 *   `chatHistoryUnavailable: true`); otherwise sets `chatError`
	 * - Race-condition guards at both the success and failure branches skip the
	 *   selection-state updates if the user navigated to a different agent
	 *   mid-flight (the entity-map update still runs so data stays cached)
	 */
	@Action(LoadAgentWithHistory)
	loadAgentWithHistory(context: StateContext<AgentsStateModel>, action: LoadAgentWithHistory): Observable<AgentDetailResponse | void> {
		context.patchState({ isDetailLoading: true });

		return this.agentsHttpService.getAgentDetail(action.agentId).pipe(
			tap((response: AgentDetailResponse) => this.applyLoadAgentSuccess(context, action, response)),
			catchError(() => {
				this.applyLoadAgentFallback(context, action);
				return of(undefined);
			})
		);
	}

	/**
	 * Saves an agent via the PATCH API and updates the entity map with the response.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing `agentId`, `name`, and `prompt`
	 * @returns Observable that completes when the state update is applied
	 *
	 * Side effects:
	 * - Serializes `configForm.triggerConfig` via `mapTriggerConfigToRuleConfig`
	 *   and includes the result as `ruleConfig` in the request body. The mapper
	 *   returns `null` when the form is incomplete, in which case
	 *   `{ ruleConfig: null }` is sent to the backend verbatim.
	 * - Upserts the returned agent into `agentEntities` and appends to `agentIds`
	 *   if it wasn't already present.
	 * - Does NOT set a loading flag — the dialog owns its own `isSaving` signal.
	 */
	@Action(SaveAgent)
	saveAgent(context: StateContext<AgentsStateModel>, action: SaveAgent): Observable<void> {
		const state: AgentsStateModel = context.getState();

		const triggerType: AgentTriggerType | undefined = state.configForm.triggerConfig.type;
		const ruleConfig: RuleConfigItem | null = mapTriggerConfigToRuleConfig(state.configForm.triggerConfig);
		const patchBody: PatchAgentBody = {
			agentId: action.agentId,
			name: action.name,
			prompt: action.prompt,
			emails: parseRecipients(state.configForm.delivery.recipients),
			// Name the rule type on every save that carries a config: the API otherwise keeps the stored rule's type,
			// so switching between a schedule and a trigger would be rejected. A type without a config is refused, so
			// an incomplete trigger sends neither.
			...(triggerType && ruleConfig && { ruleType: triggerTypeToRuleType(triggerType) }),
			ruleConfig,
			...(action.paused !== undefined && { paused: action.paused }),
		};
		return this.agentsHttpService.updateAgent(patchBody).pipe(
			tap((response: AgentsResponse) => {
				const item: AgentListItem | undefined = response.agents[0];
				if (item) {
					const agent: Agent = {
						identifier: item.agentId,
						sessionId: item.sessionId,
						name: action.name || item.name || 'Unnamed Agent',
						prompt: item.prompt,
					};
					const mapped: AgentDetail = mapListItemToAgentDetail(agent, item);
					const currentState: AgentsStateModel = context.getState();
					const agentEntities: Record<string, AgentDetail> = { ...currentState.agentEntities, [mapped.identifier]: mapped };
					const agentIds: string[] = currentState.agentIds.includes(mapped.identifier) ? currentState.agentIds : [...currentState.agentIds, mapped.identifier];
					// Clear isDirty so hasUnsavedChanges() returns false post-save. In the normal
					// flow this is invisible (the dialog closes on save and the form gets rebuilt
					// on next open), but it keeps the invariant true for any future caller that
					// reads the flag while the dialog is still mounted.
					context.patchState({
						agentEntities,
						agentIds,
						configForm: { ...currentState.configForm, isDirty: false },
					});
				}
			}),
			map(() => undefined)
		);
	}

	/**
	 * Toggles only the `paused` flag on an agent via PATCH.
	 *
	 * Used by the listing-context Pause/Resume affordance, which has no
	 * `configForm` state in scope. The PATCH body is `{ agentId, paused }`
	 * only — `name`, `prompt`, and `ruleConfig` are intentionally omitted so
	 * the backend leaves them untouched. The response is mapped through
	 * `mapListItemToAgentDetail` and merged into `agentEntities` to refresh
	 * the status badge in place without a full list refetch.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing `agentId` and the target `paused` value
	 * @returns Observable that completes when the state update is applied
	 */
	@Action(SetAgentPaused)
	setAgentPaused(context: StateContext<AgentsStateModel>, action: SetAgentPaused): Observable<void> {
		const patchBody: PatchAgentBody = {
			agentId: action.agentId,
			paused: action.paused,
		};
		const beforeIds: readonly string[] = context.getState().pausingAgentIds;
		if (!beforeIds.includes(action.agentId)) {
			context.patchState({ pausingAgentIds: [...beforeIds, action.agentId] });
		}
		return this.agentsHttpService.updateAgent(patchBody).pipe(
			tap((response: AgentsResponse) => {
				const item: AgentListItem | undefined = response.agents[0];
				if (item) {
					const agent: Agent = {
						identifier: item.agentId,
						sessionId: item.sessionId,
						name: item.name,
						prompt: item.prompt,
					};
					const mapped: AgentDetail = mapListItemToAgentDetail(agent, item);
					const currentState: AgentsStateModel = context.getState();
					context.patchState({
						agentEntities: { ...currentState.agentEntities, [mapped.identifier]: mapped },
					});
				}
			}),
			finalize(() => {
				const afterIds: readonly string[] = context.getState().pausingAgentIds;
				context.patchState({ pausingAgentIds: afterIds.filter((id: string) => id !== action.agentId) });
			}),
			map(() => undefined)
		);
	}

	/** Deletes an agent via the API and removes it from the normalized store. */
	@Action(DeleteAgent)
	deleteAgent(context: StateContext<AgentsStateModel>, action: DeleteAgent): Observable<void> {
		return this.agentsHttpService.deleteAgent(action.agentId).pipe(
			tap(() => {
				const currentState: AgentsStateModel = context.getState();
				const remainingEntities: Record<string, AgentDetail> = { ...currentState.agentEntities };
				delete remainingEntities[action.agentId];
				context.patchState({
					agentEntities: remainingEntities,
					agentIds: currentState.agentIds.filter((id: string) => id !== action.agentId),
					selectedAgentId: currentState.selectedAgentId === action.agentId ? null : currentState.selectedAgentId,
				});
			}),
			map(() => undefined)
		);
	}

	/**
	 * Duplicates an agent with a " (Copy)" suffix as a new draft.
	 *
	 * Creates a fresh chat session for the copy, then PATCHes that session
	 * with the source agent's prompt under the new name. The resulting agent
	 * is appended to the list; the source is untouched.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing the source `agentId`
	 * @returns Observable that completes when the state update is applied;
	 *   short-circuits with `of(undefined)` if the source agent isn't in the store
	 *
	 * Side effects:
	 * - Calls `createAgent` then `updateAgent` sequentially via `switchMap`
	 * - Appends the new agent to both `agentEntities` and `agentIds` on success
	 * - Does not clone `executionHistory`, chat messages, or the source's
	 *   trigger/rule config — duplicates are prompt-only drafts that the user
	 *   configures from scratch
	 */
	@Action(DuplicateAgent)
	duplicateAgent(context: StateContext<AgentsStateModel>, action: DuplicateAgent): Observable<void> {
		const state: AgentsStateModel = context.getState();
		const source: AgentDetail | undefined = state.agentEntities[action.agentId];
		if (!source) {
			return of(undefined);
		}
		const duplicateName: string = `${source.name} (Copy)`;

		return this.agentsHttpService.createAgent().pipe(
			switchMap((session: Agent) => {
				const patchBody: PatchAgentBody = { agentId: session.identifier, name: duplicateName, prompt: source.prompt };
				return this.agentsHttpService.updateAgent(patchBody).pipe(
					tap((response: AgentsResponse) => {
						const item: AgentListItem | undefined = response.agents[0];
						if (item) {
							const agent: Agent = {
								identifier: item.agentId,
								sessionId: item.sessionId,
								name: duplicateName,
								prompt: item.prompt,
							};
							const mapped: AgentDetail = mapListItemToAgentDetail(agent, item);
							const currentState: AgentsStateModel = context.getState();
							context.patchState({
								agentEntities: { ...currentState.agentEntities, [mapped.identifier]: mapped },
								agentIds: [...currentState.agentIds, mapped.identifier],
							});
						}
					})
				);
			}),
			map(() => undefined)
		);
	}

	// ==========================================
	// Builder Dialog Lifecycle Action Handlers
	// ==========================================

	/**
	 * Opens the agent builder dialog and initializes the chat session based on mode.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing `mode` (`new` | `continue` | `edit`) and
	 *   optional `agentId` (required for `continue` and `edit`)
	 * @returns `of(undefined)` for `new` (state is primed synchronously; the
	 *   agent row is minted lazily by `ensureChatSession`) or the observable
	 *   forwarded from the `LoadAgentWithHistory` dispatch for `continue`/`edit`.
	 *   Returns `of(undefined)` when `continue`/`edit` is passed without an `agentId`.
	 *
	 * Side effects:
	 * - Sets `builderMode` and resets `builderActiveTab` to `configuration`
	 * - For `new`: replaces `configForm` with defaults and clears any leftover
	 *   session ids from a prior dialog. Does NOT POST /data/v5/agents — the
	 *   agent row + session id are minted lazily by `ensureChatSession` when
	 *   the user first saves or sends a chat, so opening and closing the
	 *   dialog without action leaves no orphan row in the DB.
	 * - For `continue`/`edit`: sets `selectedAgentId` and dispatches `LoadAgentWithHistory`
	 * - Clears `chatError`, `chatHistoryUnavailable`, `previewError`, `previewContent`
	 *   in both branches
	 */
	@Action(OpenBuilderDialog)
	openBuilderDialog(context: StateContext<AgentsStateModel>, action: OpenBuilderDialog): Observable<void> {
		const mode: BuilderMode = action.mode;
		const agentId: string | undefined = action.agentId;

		if (mode === 'new') {
			context.patchState({
				builderMode: 'new',
				builderActiveTab: 'configuration',
				configForm: DEFAULT_AGENTS_STATE.configForm,
				chatSessionId: null,
				chatAgentId: null,
				chatMessages: [],
				isChatSessionLoading: false,
				chatError: null,
				chatHistoryUnavailable: false,
				previewError: null,
				previewContent: null,
				previewInlineCharts: null,
				previewWarningMessage: null,
				previewProgressMessage: null,
				chatProgressMessage: null,
			});
			return of(undefined);
		}

		if ((mode === 'continue' || mode === 'edit') && agentId) {
			context.patchState({
				builderMode: mode,
				builderActiveTab: 'configuration',
				selectedAgentId: agentId,
				chatMessages: [],
				chatError: null,
				chatHistoryUnavailable: false,
				previewError: null,
				previewContent: null,
				previewInlineCharts: null,
				previewWarningMessage: null,
				previewProgressMessage: null,
				chatProgressMessage: null,
			});
			return context.dispatch(new LoadAgentWithHistory(agentId));
		}

		return of(undefined);
	}

	/** Closes the agent builder dialog and aborts any active streams. */
	@Action(CloseBuilderDialog)
	closeBuilderDialog(context: StateContext<AgentsStateModel>): void {
		this.agentsWsService.abortStream();
		context.patchState({
			builderMode: null,
			builderActiveTab: 'configuration',
			isChatGenerating: false,
			streamingChatContent: null,
			isPreviewLoading: false,
			streamingPreviewContent: null,
			previewProgressMessage: null,
			chatProgressMessage: null,
		});
	}

	/** Sets the active tab within the builder dialog. */
	@Action(SetBuilderActiveTab)
	setBuilderActiveTab(context: StateContext<AgentsStateModel>, action: SetBuilderActiveTab): void {
		context.patchState({
			builderActiveTab: action.tab,
		});
	}

	// ==========================================
	// Config Form Action Handlers
	// ==========================================

	/** Updates the builder config form with partial changes. Resets preview when instructions change. */
	@Action(UpdateConfigForm)
	updateConfigForm(context: StateContext<AgentsStateModel>, action: UpdateConfigForm): void {
		const state: AgentsStateModel = context.getState();
		// `triggerConfig` is spread-merged so callers that pass a partial TriggerConfig
		// preserve unspecified slices. Today's callers (config-tab handlers, starter
		// templates) pass full objects, so the merge is a no-op for them; the merge
		// is defensive for future partial payloads. Other top-level fields use replace
		// semantics. To fully reset the trigger, callers must dispatch explicit empty
		// slice values.
		const updatedForm: AgentFormState = {
			...state.configForm,
			...action.changes,
			triggerConfig: action.changes.triggerConfig ? { ...state.configForm.triggerConfig, ...action.changes.triggerConfig } : state.configForm.triggerConfig,
		};
		const instructionsChanged: boolean = action.changes.instructions !== undefined && action.changes.instructions !== state.configForm.instructions;
		context.patchState({
			configForm: updatedForm,
			...(instructionsChanged ? { previewContent: null, previewError: null, previewInlineCharts: null, previewWarningMessage: null } : {}),
		});
	}

	// ==========================================
	// Preview/Invoke Action Handlers
	// ==========================================

	/**
	 * Previews the agent by streaming the prompt via WebSocket.
	 *
	 * Runs the agent's current instructions against a one-shot preview stream
	 * without persisting the result — used by the builder dialog's preview tab
	 * to show the user what their config would produce before saving.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing the prompt text to preview
	 * @returns Observable that completes when the stream terminates
	 *
	 * Side effects:
	 * - Sets `isPreviewLoading: true`, clears `previewError` and seeds `streamingPreviewContent`
	 * - Accumulates `delta` events into `streamingPreviewContent`
	 * - On `complete`: promotes the accumulated content to `previewContent`
	 * - On `error` event or transport failure: sets `previewError` with a
	 *   subtype-specific message (connection / timeout / generic)
	 * - On teardown with no terminal event at all: clears `isPreviewLoading` so the
	 *   spinner cannot outlive the run
	 */
	@Action(PreviewAgent)
	previewAgent(context: StateContext<AgentsStateModel>, action: PreviewAgent): Observable<void> {
		const runGeneration: number = ++this.previewGeneration;
		context.patchState({
			isPreviewLoading: true,
			previewError: null,
			streamingPreviewContent: '',
			previewProgressMessage: null,
			previewInlineCharts: null,
			previewWarningMessage: null,
		});

		return this.agentsWsService.sendPreviewChat(action.prompt, action.timezone).pipe(
			tap((event: AgentStreamEvent) => {
				switch (event.type) {
					case 'delta':
						context.patchState({
							streamingPreviewContent: (context.getState().streamingPreviewContent ?? '') + event.content,
						});
						break;
					case 'complete':
						// The tool label is cleared with the rest of the in-flight state: the preview tab
						// renders it only while loading or streaming, so a finished run holding one leaves
						// the store carrying a value nothing can show.
						context.patchState({
							previewContent: context.getState().streamingPreviewContent ?? '',
							previewInlineCharts: event.inlineCharts ?? null,
							previewWarningMessage: event.message ?? null,
							isPreviewLoading: false,
							streamingPreviewContent: null,
							previewProgressMessage: null,
						});
						break;
					case 'error':
						context.patchState({
							previewError: event.message || 'Preview failed. Please try again.',
							isPreviewLoading: false,
							streamingPreviewContent: null,
							previewProgressMessage: null,
							previewInlineCharts: null,
							previewWarningMessage: null,
						});
						break;
					case 'processing':
						// A labeled frame names the tool the run is currently executing; label-less
						// frames are keep-alive pings and preserve the last shown label.
						if (event.message) {
							context.patchState({ previewProgressMessage: event.message });
						}
						break;
					default: {
						// Compile-time exhaustiveness stays (`never`), but at runtime a newer backend
						// may ship frame kinds this build doesn't know — ignore them, never throw.
						const exhaustive: never = event;
						console.warn('[AgentsState] Ignoring unknown AgentStreamEvent', exhaustive);
						break;
					}
				}
			}),
			map(() => undefined),
			catchError((error: unknown) => {
				const errorMessage: string = this.translateStreamError(error, {
					connection: 'Unable to connect to the server. Please check your connection and try again.',
					timeout: 'Preview timed out. The request took too long. Please try again.',
					generic: 'Preview failed. Please try again.',
				});
				context.patchState({
					previewError: errorMessage,
					isPreviewLoading: false,
					streamingPreviewContent: null,
					previewProgressMessage: null,
					previewWarningMessage: null,
				});
				return of(undefined);
			}),
			finalize(() => {
				// A stream can end with no event at all — an abort unsubscribes it silently — which
				// would otherwise strand the spinner with no way for the user to retry (the preview
				// tab only re-runs while `isPreviewLoading` is false). The generation check keeps an
				// older run's teardown from clearing the spinner a newer run is relying on.
				if (runGeneration === this.previewGeneration && context.getState().isPreviewLoading) {
					context.patchState({ isPreviewLoading: false, streamingPreviewContent: null, previewProgressMessage: null });
				}
			})
		);
	}

	/**
	 * Invokes an active agent on demand ("Execute Now").
	 *
	 * Triggers the backend to enqueue a run, then starts polling for the result.
	 * Only a repeat dispatch for the agent already running short-circuits: the
	 * double-click guard belongs to that one row, and a run on one agent must not
	 * swallow a click on another.
	 *
	 * The POST outlives the click, so the response is only allowed to act on the
	 * UI if watching has not moved on in the meantime — see `invokeGeneration`.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing the `agentId` to invoke
	 * @returns Observable that completes once the POST lands (polling continues
	 *   in the background via a dispatched `PollInvocationResult`)
	 *
	 * Side effects:
	 * - Marks this agent as running before the API call (no-op if it already is)
	 * - Prepends an optimistic pending `ExecutionHistoryEntry` to the agent's
	 *   history so the UI shows something immediately
	 * - Records the new run as the watched one and dispatches `PollInvocationResult`
	 *   (with the agentId + invocationId) to begin the backoff poll loop (5s growing
	 *   to 15s ticks, ~15-minute budget) — both skipped for an abandoned run
	 * - Clears the running marker and rethrows on HTTP failure so the caller can
	 *   toast; an abandoned run rethrows without touching the flags
	 */
	@Action(InvokeAgent)
	invokeAgent(context: StateContext<AgentsStateModel>, action: InvokeAgent): Observable<void> {
		const state: AgentsStateModel = context.getState();
		if (state.invokingAgentId === action.agentId) {
			return of(undefined);
		}

		const invokeGeneration: number = ++this.invokeGeneration;
		context.patchState({ invokingAgentId: action.agentId, watchedInvocationId: null, pollTimedOutInvocationId: null });

		return this.agentsHttpService.invokeAgent(action.agentId).pipe(
			tap((response: InvokeAgentResponse) => {
				const currentState: AgentsStateModel = context.getState();
				const agent: AgentDetail | undefined = currentState.agentEntities[action.agentId];

				if (agent) {
					const pendingEntry: ExecutionHistoryEntry = createPendingHistoryEntry();
					const updatedAgent: AgentDetail = {
						...agent,
						executionHistory: [pendingEntry, ...agent.executionHistory],
					};
					const agentEntities: Record<string, AgentDetail> = { ...currentState.agentEntities, [action.agentId]: updatedAgent };
					context.patchState({ agentEntities });
				}

				// The run itself is real either way, so it keeps its pending entry — but if watching
				// moved on while this POST was out, it must not claim the watch back off whoever holds it.
				if (invokeGeneration !== this.invokeGeneration) {
					return;
				}

				context.patchState({ watchedInvocationId: response.invocationId });
				context.dispatch(new PollInvocationResult(action.agentId, response.invocationId));
			}),
			map(() => undefined),
			catchError((error: unknown) => {
				// Same reasoning as above: an abandoned run's failure is not the current run's to report.
				if (invokeGeneration === this.invokeGeneration) {
					context.patchState({ invokingAgentId: null, watchedInvocationId: null });
				}
				return throwError(() => error);
			})
		);
	}

	/**
	 * Polls `GET /agents/invocations/{invocationId}` until the invocation reaches a terminal status.
	 *
	 * The schedule backs off from 5s ticks to a 15s cap and gives up only after
	 * the full `POLL_TOTAL_CEILING_MS` budget (matching the backend's 900s
	 * execution cap — a run the backend may still finish is never abandoned
	 * inside that window). Cancelled early on four paths, all of them in this state:
	 * a terminal status is detected, `CancelInvocationPoll` is dispatched (the user
	 * left the page), `ClearAgentsState` is dispatched, or a later poll takes over the
	 * shared poll key. Nothing outside this state cancels that key — `stopAllPolling`
	 * only walks keys that registered a poll interval, and this poll registers none.
	 *
	 * The invocation route is cheap but young: a browser tab can outlive the API
	 * version it was served against, so after `POLL_DETAIL_FALLBACK_THRESHOLD`
	 * consecutive failures the poll switches to the agent-detail route, which every
	 * deployed backend serves. Without that, a 404 (route absent) or 500 (route
	 * present but broken) reads as "not finished yet" on every tick and the run
	 * idles all the way to the ceiling. Transient per-tick errors are swallowed so
	 * a single 502 doesn't abort the whole poll; `finalize`
	 * (`resetInvocationFlagsIfStuck`) guarantees cleanup on every termination path.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing `agentId` and `invocationId` to watch
	 * @returns Observable that completes when polling ends (either normally or cancelled)
	 */
	@Action(PollInvocationResult)
	pollInvocationResult(context: StateContext<AgentsStateModel>, action: PollInvocationResult): Observable<void> {
		// The invoke POST can land after the user walked away, or after a newer run took over, so
		// poll only the run the UI is actually watching. Anything else is a run nobody is waiting on.
		if (context.getState().watchedInvocationId !== action.invocationId) {
			return of(undefined);
		}

		const pollKey: PollRequestKey = PollRequestKey.PollInvocationResult;
		const pollRun: number = ++this.pollGeneration;
		this.cancelPollRequestService.cancelPollSubject(pollKey);
		this.cancelPollRequestService.createPollSubject(pollKey);

		// Per-run probe selection; reset by the new subscription each poll starts with.
		let consecutiveDetailFailures: number = 0;
		let useAgentDetailProbe: boolean = false;

		return from(POLL_DELAYS_MS).pipe(
			// Sequential timers produce growing gaps between ticks; exhausting the
			// delays array is the ceiling (total budget) termination path.
			concatMap((delayMs: number) => timer(delayMs)),
			takeUntil(this.cancelPollRequestService.pollSubjectsToCancel[pollKey]),
			// Step 1: Ask whether the run has finished. A non-null answer carries the
			// refreshed agent the terminal upsert needs; null means "not yet, or unknown".
			// `exhaustMap`, not `switchMap`: a tick firing while a probe is still in flight is
			// skipped rather than allowed to cancel it — against a backend slower than the gap,
			// switching would abandon every probe before it could answer.
			exhaustMap(() => {
				if (useAgentDetailProbe) {
					return this.probeViaAgentDetail(action.invocationId, action.agentId);
				}
				return this.agentsHttpService.getInvocationDetail(action.invocationId).pipe(
					tap(() => (consecutiveDetailFailures = 0)),
					switchMap((response: InvocationDetailResponse) => {
						if (!isTerminalInvocationStatus(response.invocation.status)) {
							return of(null);
						}
						// The invocation route carries no agent entity, so fetch it for the upsert.
						return this.agentsHttpService.getAgentDetail(action.agentId).pipe(
							map((detail: AgentDetailResponse) => detail.agent),
							catchError(() => {
								// The run IS done — never leave the UI blocked because the follow-up
								// entity refresh failed; the feed reload still surfaces the result.
								context.patchState({
									invokingAgentId: null,
									watchedInvocationId: null,
									settledInvocationIds: withSettledInvocation(context.getState().settledInvocationIds, action.invocationId),
								});
								this.cancelPollRequestService.cancelPollSubject(pollKey);
								context.dispatch(new GetActivityFeed());
								return of(null);
							})
						);
					}),
					catchError((error: unknown) => {
						consecutiveDetailFailures += 1;
						if (consecutiveDetailFailures >= POLL_DETAIL_FALLBACK_THRESHOLD) {
							useAgentDetailProbe = true;
							console.warn('[AgentsState] Invocation detail route unavailable; polling the agent detail route instead', error);
						}
						return of(null);
					})
				);
			}),
			// Step 2: A terminal answer ends the poll and upserts the run's agent.
			tap((agent: AgentDetailItem | null) => {
				if (agent) {
					this.applyTerminalInvocation(context, agent, action.agentId, action.invocationId, pollKey);
				}
			}),
			map(() => undefined),
			// Step 3: Safety net — on any termination path where Step 2 didn't
			// already clear the running marker (budget exhausted, external cancel, or
			// error bubbling through), reset it here so the UI unblocks. A poll that has
			// since been replaced owns none of that state any more and stays out of it.
			finalize(() => {
				if (pollRun === this.pollGeneration) {
					this.resetInvocationFlagsIfStuck(context, action.invocationId, pollKey);
				}
			}),
			catchError(() => of(undefined))
		);
	}

	/**
	 * Stops an in-flight invocation poll because the user stopped watching it.
	 *
	 * Clears the running marker *before* cancelling, so the poll's `finalize` sees an
	 * already-unblocked UI and doesn't mistake a deliberate stop for the poll
	 * running out its budget.
	 */
	@Action(CancelInvocationPoll)
	cancelInvocationPoll(context: StateContext<AgentsStateModel>): void {
		this.invokeGeneration += 1;
		context.patchState({ invokingAgentId: null, watchedInvocationId: null });
		this.cancelPollRequestService.cancelPollSubject(PollRequestKey.PollInvocationResult);
	}

	/**
	 * Hides the "still running" notice at the user's request.
	 *
	 * Visibility only: the run stays in `settledInvocationIds`, so acknowledging the message
	 * cannot hand that run another poll budget on the next feed load.
	 */
	@Action(DismissInvocationPollNotice)
	dismissInvocationPollNotice(context: StateContext<AgentsStateModel>): void {
		context.patchState({ pollTimedOutInvocationId: null });
	}

	// ==========================================
	// Activity Action Handlers
	// ==========================================

	/**
	 * Fetches the activity feed from the cross-agent invocations endpoint, honoring the
	 * current `activityFilterAgentIds`. Clears `activityFeedError` before the call so a
	 * retry after a prior failure doesn't flash the error state, sets it on failure so
	 * the page can render a dedicated error + retry card instead of the true-empty
	 * "No agent activity yet?" state.
	 *
	 * The feed is also the answer to a run we gave up polling: if it comes back finished,
	 * the "still running" notice has served its purpose and is retired here.
	 */
	@Action(GetActivityFeed)
	getActivityFeed(context: StateContext<AgentsStateModel>): Observable<void> {
		context.patchState({ isActivityLoading: true, activityFeedError: false });
		const agentIds: readonly string[] = context.getState().activityFilterAgentIds;
		return this.agentsHttpService.getRecentInvocations(agentIds).pipe(
			tap((response: AgentInvocationsResponse) => {
				const invocations: readonly AgentInvocationDetailItem[] = response.invocations ?? [];
				context.patchState({
					activityGroups: mapInvocationsToActivityGroups(invocations),
					isActivityLoading: false,
					activityFeedError: false,
					pollTimedOutInvocationId: this.stillRunningInvocationId(context.getState().pollTimedOutInvocationId, invocations),
				});
				this.rearmInvocationWatch(context, invocations);
			}),
			map(() => undefined),
			catchError(() => {
				context.patchState({ isActivityLoading: false, activityFeedError: true });
				return of(undefined);
			})
		);
	}

	/** Updates the agent-filter for the activity feed and re-fetches so the list reflects the new filter. */
	@Action(SetActivityAgentFilter)
	setActivityAgentFilter(context: StateContext<AgentsStateModel>, action: SetActivityAgentFilter): Observable<void> {
		context.patchState({ activityFilterAgentIds: action.agentIds });
		return context.dispatch(new GetActivityFeed());
	}

	/** Selects an activity entry by ID and fetches its full invocation detail (inline charts live only on the detail route). */
	@Action(SelectActivity)
	selectActivity(context: StateContext<AgentsStateModel>, action: SelectActivity): Observable<void> {
		// Always clear the previous detail so a reopened panel renders from a fresh fetch, never a stale one.
		context.patchState({ selectedActivityId: action.activityId, selectedInvocationDetail: null });
		if (!action.activityId) {
			return of(undefined);
		}
		// Activity entry IDs are invocation IDs (see mapInvocationsToActivityGroups).
		return context.dispatch(new GetInvocationDetail(action.activityId));
	}

	/**
	 * Fetches the full detail for one invocation.
	 *
	 * Failures (404 on pre-detail-route rows, transient errors) leave the detail
	 * null so the panel falls back to the markdown already loaded from the list —
	 * charts are an enhancement, never a blocker.
	 */
	@Action(GetInvocationDetail)
	getInvocationDetail(context: StateContext<AgentsStateModel>, action: GetInvocationDetail): Observable<void> {
		const detailRun: number = ++this.detailGeneration;
		context.patchState({ isInvocationDetailLoading: true });
		return this.agentsHttpService.getInvocationDetail(action.invocationId).pipe(
			tap((response: InvocationDetailResponse) => {
				// Only apply if this invocation is still the selected activity — a slow
				// response must not overwrite the detail of a newer selection.
				if (context.getState().selectedActivityId === action.invocationId) {
					context.patchState({ selectedInvocationDetail: response.invocation });
				}
				// Only the newest fetch may take the shared loading flag down — see `detailGeneration`.
				if (detailRun === this.detailGeneration) {
					context.patchState({ isInvocationDetailLoading: false });
				}
			}),
			map(() => undefined),
			catchError(() => {
				if (detailRun === this.detailGeneration) {
					context.patchState({ isInvocationDetailLoading: false });
				}
				return of(undefined);
			})
		);
	}

	/** Sets the selected panel type for the detail view. */
	@Action(SetSelectedPanelType)
	setSelectedPanelType(context: StateContext<AgentsStateModel>, action: SetSelectedPanelType): void {
		context.patchState({ selectedPanelType: action.panelType });
	}

	// ==========================================
	// Detail Panel Action Handlers
	// ==========================================

	/** Shows the right-hand detail panel. */
	@Action(OpenDetailPanel)
	openDetailPanel(context: StateContext<AgentsStateModel>): void {
		context.patchState({ detailPanelOpen: true });
	}

	/** Hides the right-hand detail panel. */
	@Action(CloseDetailPanel)
	closeDetailPanel(context: StateContext<AgentsStateModel>): void {
		context.patchState({ detailPanelOpen: false });
	}

	// ==========================================
	// Private Helpers
	// ==========================================

	/**
	 * Builds an internal `Agent` from an API response item.
	 *
	 * Resolves the display name using `item.name` first, then the optional
	 * `nameFallback`, then the `'Unnamed Agent'` placeholder. Use this when
	 * the API's echoed name should take priority; for call sites that want
	 * to override with an external name (e.g. a save-dialog input), construct
	 * the object inline instead.
	 *
	 * @param item - API item with the 4 shared fields (`agentId` / `sessionId` / `name` / `prompt`)
	 * @param nameFallback - Optional name used when `item.name` is falsy
	 * @returns Internal `Agent` record
	 */
	private buildAgentFromApiItem(item: AgentApiSource, nameFallback?: string | null): Agent {
		const resolvedName: string = item.name || nameFallback || 'Unnamed Agent';
		return {
			identifier: item.agentId,
			sessionId: item.sessionId,
			name: resolvedName,
			prompt: item.prompt,
		};
	}

	/**
	 * Builds the normalized entity map + ordered ID array from a list of agents.
	 *
	 * Pairs `agentEntities` (for O(1) lookups) with `agentIds` (for ordered
	 * list rendering) so the two stay in sync. Callers that need to preserve
	 * fields from the prior entity map (e.g. `executionHistory` in `getAgents`)
	 * should reconcile those before calling this helper.
	 *
	 * @param agents - Denormalized agents to store
	 * @returns Object with `entities` map and `ids` array in `agents` order
	 */
	private normalizeAgents(agents: readonly AgentDetail[]): { entities: Record<string, AgentDetail>; ids: string[] } {
		const entities: Record<string, AgentDetail> = {};
		const ids: string[] = [];
		for (const agent of agents) {
			entities[agent.identifier] = agent;
			ids.push(agent.identifier);
		}
		return { entities, ids };
	}

	/**
	 * Translates a WebSocket stream error into a user-facing message.
	 *
	 * Both the chat and preview streams share the same error-subtype taxonomy
	 * (`WebSocketConnectionError`, `TimeoutError`, then fallback). Only the
	 * displayed strings differ per caller, so callers pass their own message
	 * set rather than sharing a single constant.
	 *
	 * @param error - The thrown value from the stream's `catchError`
	 * @param messages - User-facing message for each known error subtype
	 * @returns The selected user-facing message
	 */
	private translateStreamError(error: unknown, messages: { connection: string; timeout: string; generic: string }): string {
		if (error instanceof WebSocketConnectionError) {
			return messages.connection;
		}
		if (error instanceof TimeoutError) {
			return messages.timeout;
		}
		return messages.generic;
	}

	/**
	 * Appends the user's prompt to `chatMessages` optimistically and primes the
	 * streaming state (`isChatGenerating: true`, empty `streamingChatContent`,
	 * cleared `chatError`).
	 *
	 * The message append is intentionally not reverted on failure — if a later
	 * error fires, the user sees their own message preserved alongside the
	 * error state so they can retry without re-typing.
	 *
	 * @param context - NGXS state context
	 * @param prompt - The user's message text
	 */
	private applyOptimisticUserMessage(context: StateContext<AgentsStateModel>, prompt: string): void {
		const state: AgentsStateModel = context.getState();
		const userMessage: ParsedChatMessage = {
			role: ChatRole.user,
			content: prompt,
			textContent: prompt,
		};
		context.patchState({
			chatMessages: [...state.chatMessages, userMessage],
			isChatGenerating: true,
			chatError: null,
			streamingChatContent: '',
			// Reset here, not only on each teardown path: this is the one place a run begins, so a
			// label can never outlive the answer it described even if a future exit path forgets to
			// clear it. Until the new run's first frame lands the spinner row would otherwise name a
			// tool this question never ran.
			chatProgressMessage: null,
		});
	}

	/**
	 * Applies a single streaming delta. Appends raw content to `streamingChatContent`,
	 * and when the buffer contains an in-progress `<finprompt>` block, also writes
	 * the partial body into `configForm.instructions` so the textarea fills live as
	 * the AI types. Final parsing (name extraction, preview reset) still happens on
	 * `complete`.
	 *
	 * @param context - NGXS state context
	 * @param deltaContent - The new content chunk from the WebSocket delta event
	 */
	private applyChatDelta(context: StateContext<AgentsStateModel>, deltaContent: string): void {
		const currentState: AgentsStateModel = context.getState();
		const newRawContent: string = (currentState.streamingChatContent ?? '') + deltaContent;
		const partialFinprompt: string | null = extractStreamingFinprompt(newRawContent);
		if (partialFinprompt !== null) {
			context.patchState({
				streamingChatContent: newRawContent,
				chatProgressMessage: null,
				configForm: {
					...currentState.configForm,
					instructions: partialFinprompt,
					isDirty: true,
				},
			});
			return;
		}
		// Text is streaming now — the tool-progress label is superseded.
		context.patchState({ streamingChatContent: newRawContent, chatProgressMessage: null });
	}

	/**
	 * Finalizes a completed chat stream.
	 *
	 * Promotes `streamingChatContent` to a parsed assistant message. When the
	 * reply contains a `finprompt`, also updates `configForm.instructions` and
	 * extracts a name from the response text (only if the current name is
	 * empty), then resets `previewContent` / `previewError` so the preview tab
	 * doesn't show stale output against the new instructions.
	 *
	 * @param context - NGXS state context
	 */
	private applyChatStreamComplete(context: StateContext<AgentsStateModel>, inlineCharts?: Readonly<Record<string, InlineChartData>> | null): void {
		const currentState: AgentsStateModel = context.getState();
		const accumulatedContent: string = currentState.streamingChatContent ?? '';
		const chatMessage: ChatMessage = { role: ChatRole.assistant, content: accumulatedContent };
		const parsedMessage: ParsedChatMessage = { ...parseChatMessage(chatMessage), ...(inlineCharts ? { inlineCharts } : {}) };

		let updatedConfigForm: AgentFormState = currentState.configForm;

		if (parsedMessage.finprompt) {
			const autoName: string = currentState.configForm.name || extractAgentName(parsedMessage.textContent);
			updatedConfigForm = {
				...currentState.configForm,
				name: autoName,
				instructions: parsedMessage.finprompt,
				isDirty: true,
			};
		}

		context.patchState({
			chatMessages: [...currentState.chatMessages, parsedMessage],
			configForm: updatedConfigForm,
			isChatGenerating: false,
			streamingChatContent: null,
			chatProgressMessage: null,
			...(parsedMessage.finprompt ? { previewContent: null, previewError: null, previewInlineCharts: null } : {}),
		});
	}

	/**
	 * Handles a transport-level WebSocket failure from `sendChatMessage`.
	 *
	 * Distinct from the in-band `'error'` event (which carries a server-provided
	 * message); this path fires when the connection itself fails or times out.
	 * Routes the raw error through `translateStreamError` for a subtype-specific
	 * user-facing message and clears the streaming flags.
	 *
	 * @param context - NGXS state context
	 * @param error - The thrown value from the stream's `catchError`
	 */
	private applyChatStreamFatalError(context: StateContext<AgentsStateModel>, error: unknown): void {
		const errorMessage: string = this.translateStreamError(error, {
			connection: 'Unable to connect to the server. Please check your connection and try again.',
			timeout: 'The request timed out. The server may be experiencing issues. Please try again.',
			generic: 'Something went wrong. Please try again.',
		});
		context.patchState({
			chatMessages: this.transcriptWithStreamedPartial(context.getState()),
			chatError: errorMessage,
			isChatGenerating: false,
			streamingChatContent: null,
			chatProgressMessage: null,
		});
	}

	/**
	 * Commits whatever text had already streamed as a partial assistant message.
	 *
	 * Every path that ends a chat turn short of its terminal frame goes through here — a
	 * deliberate stop, an error frame, a dropped connection. The user watched that text
	 * arrive and the store holds the only copy, so replacing it with a banner loses it.
	 * Finprompt parsing is deliberately skipped so a half-emitted block cannot populate
	 * `configForm.instructions` with malformed output.
	 *
	 * @param state - State as it stands, with the turn's streamed text still buffered
	 * @returns The transcript with the partial appended, or unchanged when nothing streamed
	 */
	private transcriptWithStreamedPartial(state: AgentsStateModel): ParsedChatMessage[] {
		const streamed: string = state.streamingChatContent ?? '';
		if (!streamed.length) {
			return state.chatMessages;
		}
		const partialMessage: ParsedChatMessage = { role: ChatRole.assistant, content: streamed, textContent: streamed };
		return [...state.chatMessages, partialMessage];
	}

	/**
	 * Applies a successful `LoadAgentWithHistory` response to state.
	 *
	 * Always upserts the agent into `agentEntities` so the data is cached,
	 * even if the user has navigated away mid-request. Only applies the
	 * selection + session + config-form updates when the user still has this
	 * agent selected (the race-condition guard) — otherwise the entity-map
	 * update alone keeps the data fresh without clobbering the current view.
	 *
	 * @param context - NGXS state context
	 * @param action - Original action containing the requested `agentId`
	 * @param response - Successful API response to apply
	 */
	private applyLoadAgentSuccess(context: StateContext<AgentsStateModel>, action: LoadAgentWithHistory, response: AgentDetailResponse): void {
		const detail: AgentDetailItem = response.agent;

		const state: AgentsStateModel = context.getState();
		const localAgent: AgentDetail | undefined = state.agentEntities[action.agentId];
		const agent: Agent = this.buildAgentFromApiItem(detail, localAgent?.name);
		const agentDetail: AgentDetail = mapDetailItemToAgentDetail(agent, detail);
		// Per builder-dialog Figma prototype: edit/continue start with an empty chat — don't replay history.
		// Config form is sourced directly from the agent entity (authoritative saved values), not from the last chat finprompt.
		const configForm: AgentFormState = {
			name: agent.name || '',
			instructions: detail.prompt ?? agentDetail.prompt ?? '',
			triggerConfig: agentDetail.triggerConfig,
			delivery: { recipients: formatRecipients(agentDetail.emails) },
			isDirty: false,
			isValid: false,
		};

		// Upsert the entity map even if the user has moved on — keeps data cached.
		const agentEntities: Record<string, AgentDetail> = {
			...context.getState().agentEntities,
			[agentDetail.identifier]: agentDetail,
		};

		const isStillSelected: boolean = context.getState().selectedAgentId === action.agentId;
		if (isStillSelected) {
			context.patchState({
				agentEntities,
				selectedAgentId: action.agentId,
				chatSessionId: detail.sessionId || null,
				chatAgentId: detail.agentId,
				chatMessages: [],
				isChatGenerating: false,
				isChatSessionLoading: false,
				chatError: null,
				chatHistoryUnavailable: false,
				chatProgressMessage: null,
				configForm,
				isDetailLoading: false,
			});
		} else {
			context.patchState({ agentEntities });
		}
	}

	/**
	 * Applies the fallback branch when `getAgentDetail` fails.
	 *
	 * No-op if the user has navigated to a different agent (race guard). If a
	 * cached copy exists locally, rehydrates the form from that entity with
	 * `chatHistoryUnavailable: true` so the UI can surface a warning. If no
	 * cached copy exists, sets a generic `chatError`.
	 *
	 * @param context - NGXS state context
	 * @param action - Original action containing the requested `agentId`
	 */
	private applyLoadAgentFallback(context: StateContext<AgentsStateModel>, action: LoadAgentWithHistory): void {
		const currentState: AgentsStateModel = context.getState();
		const isStillSelected: boolean = currentState.selectedAgentId === action.agentId;

		if (!isStillSelected) {
			return;
		}

		const localAgent: AgentDetail | undefined = currentState.agentEntities[action.agentId];
		if (localAgent) {
			const configForm: AgentFormState = {
				name: localAgent.name || '',
				instructions: localAgent.prompt || '',
				triggerConfig: localAgent.triggerConfig,
				delivery: { recipients: formatRecipients(localAgent.emails) },
				isDirty: false,
				isValid: false,
			};
			context.patchState({
				selectedAgentId: action.agentId,
				chatSessionId: localAgent.sessionId || null,
				chatAgentId: localAgent.identifier,
				chatMessages: [],
				isChatGenerating: false,
				isChatSessionLoading: false,
				chatError: null,
				chatHistoryUnavailable: true,
				chatProgressMessage: null,
				configForm,
				isDetailLoading: false,
			});
		} else {
			context.patchState({
				chatError: 'Failed to load agent data. Please try again.',
				isDetailLoading: false,
			});
		}
	}

	/**
	 * Applies a terminal-status poll tick.
	 *
	 * Upserts the refreshed agent into `agentEntities`, clears the running marker, settles the run,
	 * cancels the poll subject, and dispatches `GetActivityFeed` so the activity panel reflects the
	 * completed run.
	 *
	 * @param context - NGXS state context
	 * @param detail - Refreshed `AgentDetailItem` carrying the terminal status
	 * @param agentId - Identifier of the agent whose invocation terminated
	 * @param invocationId - Identifier of the invocation that terminated
	 * @param pollKey - Cancel-poll-request key to dismiss on completion
	 */
	private applyTerminalInvocation(
		context: StateContext<AgentsStateModel>,
		detail: AgentDetailItem,
		agentId: string,
		invocationId: string,
		pollKey: PollRequestKey
	): void {
		const currentState: AgentsStateModel = context.getState();
		const localAgent: AgentDetail | undefined = currentState.agentEntities[agentId];
		const agent: Agent = this.buildAgentFromApiItem(detail, localAgent?.name);
		const agentDetail: AgentDetail = mapDetailItemToAgentDetail(agent, detail);
		const agentEntities: Record<string, AgentDetail> = { ...currentState.agentEntities, [agentId]: agentDetail };

		context.patchState({
			agentEntities,
			invokingAgentId: null,
			watchedInvocationId: null,
			// The feed reload below can still list this run as in flight; without this the
			// re-arm would put a finished run straight back on the poll.
			settledInvocationIds: withSettledInvocation(currentState.settledInvocationIds, invocationId),
		});

		this.cancelPollRequestService.cancelPollSubject(pollKey);
		context.dispatch(new GetActivityFeed());
	}

	/**
	 * Restores the invocation watch when a feed load reveals a run still in flight.
	 *
	 * Leaving the page deliberately forgets the watch (`CancelInvocationPoll`), so
	 * without this a user returning mid-run sees the run frozen at Pending with no
	 * live tracking until a manual refresh. When the freshly loaded feed contains a
	 * non-terminal run, re-arm the existing machinery: mark it watched and start the
	 * poll, exactly as the original Execute-now path does.
	 *
	 * No-ops when a watch or invoke is already active (never steal a live watch), and never
	 * re-arms a run the poll has already settled — one it saw finish, or one it gave up on.
	 * With several runs in flight, `newestRun` picks the one the user acted on last.
	 *
	 * @param context - NGXS state context
	 * @param invocations - Invocations the feed just returned
	 */
	private rearmInvocationWatch(context: StateContext<AgentsStateModel>, invocations: readonly AgentInvocationDetailItem[]): void {
		const state: AgentsStateModel = context.getState();
		if (state.invokingAgentId !== null || state.watchedInvocationId !== null) {
			return;
		}
		const inFlight: AgentInvocationDetailItem[] = invocations.filter(
			(invocation: AgentInvocationDetailItem) => !isTerminalInvocationStatus(invocation.status) && !state.settledInvocationIds.includes(invocation.invocationId)
		);
		if (inFlight.length === 0) {
			return;
		}
		const newest: AgentInvocationDetailItem = newestRun(inFlight);
		context.patchState({ invokingAgentId: newest.agent.agentId, watchedInvocationId: newest.invocationId });
		context.dispatch(new PollInvocationResult(newest.agent.agentId, newest.invocationId));
	}

	/**
	 * Decides whether the "still running" notice is still warranted after a feed refresh.
	 *
	 * @param timedOutInvocationId - Invocation the poll gave up on, or null when none
	 * @param invocations - Invocations the feed just returned
	 * @returns The same id while that run is absent or unfinished, else null to retire the notice
	 */
	private stillRunningInvocationId(timedOutInvocationId: string | null, invocations: readonly AgentInvocationDetailItem[]): string | null {
		if (!timedOutInvocationId) {
			return null;
		}
		const watched: AgentInvocationDetailItem | undefined = invocations.find(
			(invocation: AgentInvocationDetailItem) => invocation.invocationId === timedOutInvocationId
		);
		// Absent from the feed is not evidence it finished — the feed is windowed and filtered.
		return watched && isTerminalInvocationStatus(watched.status) ? null : timedOutInvocationId;
	}

	/**
	 * Polls the agent-detail route for the watched invocation's status.
	 *
	 * The fallback probe: every deployed backend serves this route, and its response
	 * already carries the agent entity the terminal upsert needs, so no second request.
	 *
	 * @param invocationId - Identifier of the invocation being watched
	 * @param agentId - Identifier of the agent that owns it
	 * @returns The refreshed agent once the invocation is terminal, else null
	 */
	private probeViaAgentDetail(invocationId: string, agentId: string): Observable<AgentDetailItem | null> {
		return this.agentsHttpService.getAgentDetail(agentId).pipe(
			map((response: AgentDetailResponse) => {
				const detail: AgentDetailItem = response.agent;
				const watched: AgentInvocationItem | undefined = (detail.invocations || []).find(
					(invocation: AgentInvocationItem) => invocation.invocationId === invocationId
				);
				return watched && isTerminalInvocationStatus(watched.status) ? detail : null;
			}),
			catchError(() => of(null))
		);
	}

	/**
	 * Safety-net cleanup for `pollInvocationResult`'s `finalize` stage — runs on every
	 * termination path.
	 *
	 * If this poll's run is still the watched one with an agent still marked as running,
	 * the terminal branch never fired: clear the marker to unblock the UI, point the
	 * "still running" notice at the run, and settle it so no later feed load re-arms it.
	 * A deliberate stop (`CancelInvocationPoll`) drops the watch first precisely so it
	 * doesn't land here. Always cancels the poll subject so the cancel-poll-request
	 * service doesn't keep the key registered. Only the current poll may call this: a
	 * superseded one would cancel its successor's subject and report its successor's run.
	 *
	 * @param context - NGXS state context
	 * @param invocationId - Identifier of the invocation that was being watched
	 * @param pollKey - Cancel-poll-request key to dismiss
	 */
	private resetInvocationFlagsIfStuck(context: StateContext<AgentsStateModel>, invocationId: string, pollKey: PollRequestKey): void {
		const finalState: AgentsStateModel = context.getState();
		if (finalState.invokingAgentId !== null && finalState.watchedInvocationId === invocationId) {
			// The terminal branch never fired: the poll budget elapsed (or the poll was
			// cancelled) with the run still non-terminal. The run may well still finish
			// server-side, so say so instead of stalling silently — the optimistic
			// pending entry stays accurate and a later feed refresh picks up the result.
			context.patchState({
				invokingAgentId: null,
				watchedInvocationId: null,
				pollTimedOutInvocationId: invocationId,
				settledInvocationIds: withSettledInvocation(finalState.settledInvocationIds, invocationId),
			});
		}
		this.cancelPollRequestService.cancelPollSubject(pollKey);
	}
}
